// MCHD Room Scheduler - Block Modal Component
// Modal for creating and editing time blocks

import React from 'react';
import {
  Modal,
  Stack,
  Text,
  Group,
  Button,
  Alert,
  Loader,
  Box,
  Divider,
  Tooltip,
  ActionIcon
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconCalendar,
  IconClock,
  IconMapPin,
  IconInfoCircle
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useCreateBlock, useUpdateBlock } from '../../hooks/api/useBlocks';
import { BlockForm } from '../forms/BlockForm';
import type { CreateBlockData, BlockWithStats } from '../../api/endpoints/blocks';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

interface BlockModalProps {
  opened: boolean;
  onClose: () => void;
  roomId?: string;
  block?: BlockWithStats | null;
  onSuccess?: () => void;
}

/**
 * M1: Block Modal - Create and edit time blocks
 * 
 * Features:
 * - Create new time blocks
 * - Edit existing blocks
 * - Form validation and error handling
 * - Proper loading states
 * - Accessibility support
 */
const BlockModal: React.FC<BlockModalProps> = ({
  opened,
  onClose,
  roomId,
  block,
  onSuccess
}) => {
  // =============================================================================
  // STATE
  // =============================================================================
  
  const [submitting, setSubmitting] = React.useState(false);

  // =============================================================================
  // HOOKS
  // =============================================================================
  
  const createBlockMutation = useCreateBlock();
  const updateBlockMutation = useUpdateBlock();

  // =============================================================================
  // HANDLERS
  // =============================================================================

  /**
   * Handle block creation/update
   */
  const handleSubmit = async (data: CreateBlockData) => {
    setSubmitting(true);
    
    try {
      if (block) {
        // Update existing block
        await updateBlockMutation.mutateAsync({
          id: block.id,
          data: data
        });
        
        notifications.show({
          title: 'Success',
          message: 'Block updated successfully',
          color: 'green',
          icon: <IconCheck size={16} />
        });
      } else {
        // Create new block
        await createBlockMutation.mutateAsync({
          ...data,
          roomId: roomId // Associate with room if provided
        });
        
        notifications.show({
          title: 'Success',
          message: 'Block created successfully',
          color: 'green',
          icon: <IconCheck size={16} />
        });
      }
      
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Block operation error:', error);
      
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to save block',
        color: 'red',
        icon: <IconX size={16} />
      });
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs" align="center">
          <IconCalendar size={20} />
          <Text fw={600}>
            {block ? 'Edit Time Block' : 'Create Time Block'}
          </Text>
        </Group>
      }
      size="lg"
      closeOnClickOutside={!submitting}
      closeOnEscape={!submitting}
      withCloseButton={!submitting}
      centered
      styles={{
        header: {
          backgroundColor: 'var(--mantine-color-blue-0)',
          borderBottom: '1px solid var(--mantine-color-blue-2)',
        },
        title: {
          color: 'var(--mantine-color-blue-8)',
        },
      }}
    >
      <Stack gap="md">
        {/* Info Alert */}
        <Alert
          icon={<IconInfoCircle size={16} />}
          title="Time Block Information"
          color="blue"
          variant="light"
        >
          <Text size="sm">
            Time blocks are periods when a room is unavailable for scheduling. 
            This could be for maintenance, cleaning, or other purposes.
            {roomId && ' This block will be associated with the current room.'}
          </Text>
        </Alert>

        {/* Block Form */}
        <Box>
          <BlockForm
            block={block}
            onSubmit={handleSubmit}
            onCancel={handleClose}
            isLoading={submitting}
          />
        </Box>

        {/* Loading Overlay */}
        {submitting && (
          <Box
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
          >
            <Stack align="center" gap="md">
              <Loader size="lg" />
              <Text size="sm" c="dimmed">
                {block ? 'Updating block...' : 'Creating block...'}
              </Text>
            </Stack>
          </Box>
        )}
      </Stack>
    </Modal>
  );
};

export default BlockModal;
